import React, { useState, useEffect, useRef } from 'react';
import { residentsAPI } from '../../services/api';

// Utility to capitalize strings
const capitalize = (str) =>
  typeof str === 'string' && str.length > 0
    ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    : '';

const ResidentAutocomplete = ({ value, onChange, onManualAdd, disabled }) => {
  const getDisplayName = (val) => {
    if (!val) return '';
    if (val.first_name && val.last_name) return `${capitalize(val.first_name)} ${capitalize(val.last_name)}`;
    if (val.name) return val.name;
    return '';
  };

  const [input, setInput] = useState(getDisplayName(value));
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setInput(getDisplayName(value));
  }, [value]);

  useEffect(() => {
    if (input.length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const { data } = await residentsAPI.getAll({ search: input, per_page: 5 });
        setOptions(data.data || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [input]);

  const handleSelect = (resident) => {
    onChange({
      ...resident,
      name: resident.first_name && resident.last_name ? `${capitalize(resident.first_name)} ${capitalize(resident.last_name)}` : resident.name || ''
    });
    setShowDropdown(false);
  };

  const handleManualAdd = () => {
    if (!input.trim()) return;
    onManualAdd(input);
    setShowDropdown(false);
  };

  // Hide dropdown if focus leaves both input and dropdown
  const handleBlur = (e) => {
    setTimeout(() => {
      if (
        document.activeElement !== inputRef.current &&
        (!dropdownRef.current || !dropdownRef.current.contains(document.activeElement))
      ) {
        setShowDropdown(false);
      }
    }, 100);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        className="mt-1 block w-full border-2 border-gray-200 rounded-lg p-4 text-gray-900 placeholder-gray-500 transition-all duration-200 group-hover:border-gray-300"
        value={input}
        onChange={e => {
          setInput(e.target.value);
          if (value) onChange(null);
          setShowDropdown(true);
        }}
        placeholder="Search resident by name..."
        autoComplete="off"
        disabled={disabled}
        onFocus={() => setShowDropdown(true)}
        onBlur={handleBlur}
      />
      {showDropdown && input.length >= 2 && (
        <div
          ref={dropdownRef}
          className="absolute z-10 bg-white border border-gray-200 rounded shadow w-full mt-1 max-h-48 overflow-auto"
          tabIndex={-1}
          onBlur={handleBlur}
        >
          {loading && <div className="p-4 text-md text-gray-400">Searching...</div>}
          {!loading && options.length === 0 && (
            <div className="p-4 text-md text-gray-400">No resident found. <button className="text-blue-500 underline ml-1" onClick={handleManualAdd}>Add as non-resident</button></div>
          )}
          {options.map(resident => (
            <div
              key={resident.id}
              className="p-4 text-md hover:bg-blue-50 cursor-pointer"
              onClick={() => handleSelect(resident)}
            >
              {capitalize(resident.first_name)} {capitalize(resident.last_name)} {resident.birthdate ? <span className="text-xs text-gray-400">({resident.birthdate})</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResidentAutocomplete;
